import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Paper, List, ListItem, ListItemText, Link as MuiLink, Divider, Grid, Card, CardMedia, CardContent, Button } from '@mui/material';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from '../api/axios';
import LinkIcon from '@mui/icons-material/Link';
import SchoolIcon from '@mui/icons-material/School';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DecorativeCubes from '../components/DecorativeCubes';

const getFullMediaSrc = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/uploads')) return url;
  if (url.startsWith('uploads')) return `/${url}`;
  if (url.startsWith('/')) return url;
  return url;
};

const getVideoMimeType = (url) => {
  if (!url) return 'video/mp4';
  const ext = (url.split('.').pop() || '').toLowerCase();
  const types = {
    mp4: 'video/mp4',
    m4v: 'video/mp4',
    webm: 'video/webm',
    ogg: 'video/ogg',
    mov: 'video/quicktime',
  };
  return types[ext] || 'video/mp4';
};

const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const match = url.match(regExp);
  const videoId = (match && match[7].length === 11) ? match[7] : null;
  if (videoId) {
    return `https://www.youtube.com/embed/${videoId}`;
  }

  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }

  return null;
};

const isYandexDisk = (url) => {
  if (!url) return false;
  return /disk\.yandex\.(ru|com|kz|by|uz|am)|disk\.360\.yandex|yadi\.sk/i.test(url);
};

const isDirectVideo = (url) => {
  if (!url) return false;
  return url.startsWith('/uploads') ||
    url.startsWith('uploads') ||
    /\.(mp4|webm|ogg|m4v|mov)$/i.test(url);
};

const ConsultationVideoPlayer = ({ videoUrl, title }) => {
  const [useFallback, setUseFallback] = useState(false);
  const embedUrl = getYouTubeEmbedUrl(videoUrl);

  if (embedUrl) {
    return (
      <Box sx={{ position: 'relative', pt: '56.25%', backgroundColor: '#000' }}>
        <Box
          component="iframe"
          src={embedUrl}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 0,
          }}
        />
      </Box>
    );
  }

  if (isYandexDisk(videoUrl)) {
    const streamUrl = `${window.location.origin}/api/yandex-stream?url=${encodeURIComponent(videoUrl)}`;

    if (!useFallback) {
      return (
        <Box sx={{ position: 'relative', pt: '56.25%', backgroundColor: '#000' }}>
          <Box
            component="video"
            controls
            playsInline
            preload="metadata"
            onError={() => setUseFallback(true)}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            }}
          >
            <source src={streamUrl} type="video/mp4" />
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 3, textAlign: 'center', backgroundColor: 'rgba(0, 172, 193, 0.08)' }}>
        <VideocamIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Button
          variant="contained"
          href={videoUrl}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<OpenInNewIcon />}
        >
          Открыть видео
        </Button>
      </Box>
    );
  }

  if (isDirectVideo(videoUrl)) {
    const src = getFullMediaSrc(videoUrl);
    return (
      <Box sx={{ position: 'relative', pt: '56.25%', backgroundColor: '#000' }}>
        <Box
          component="video"
          controls
          playsInline
          preload="metadata"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
          }}
        >
          <source src={src} type={getVideoMimeType(src)} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, textAlign: 'center', backgroundColor: 'rgba(0, 172, 193, 0.08)' }}>
      <VideocamIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
      <Button
        variant="contained"
        href={videoUrl}
        target="_blank"
        rel="noopener noreferrer"
        startIcon={<OpenInNewIcon />}
      >
        Смотреть видео
      </Button>
    </Box>
  );
};

const hasVisualLayout = (items) =>
  items.some((item) => item.imageUrl || item.mediaType === 'video');

const ConsultationsListPage = () => {
  const [consultationsGrouped, setConsultationsGrouped] = useState({});
  const [loading, setLoading] = useState(true);

  const categoryIcons = {
    'physical-instructor': '🏃',
    'music-director': '🎵',
    'speech-therapist': '🗣️',
    'psychologist': '🧠',
    'educator': '👨‍🏫',
    'general': '📚',
  };

  useEffect(() => {
    fetchConsultations();
  }, []);

  const fetchConsultations = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/consultations/public');
      setConsultationsGrouped(response.data);
    } catch (error) {
      console.error('Ошибка загрузки консультаций:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <Container>
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Typography variant="h6">Загрузка...</Typography>
          </Box>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <DecorativeCubes minCubes={7} maxCubes={11} />
      <Header />
      <Box sx={{ py: 8, backgroundColor: '#f9f9f9', minHeight: 'calc(100vh - 200px)', position: 'relative', zIndex: 2 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <SchoolIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography
              variant="h2"
              sx={{
                mb: 2,
                fontWeight: 700,
                color: 'primary.main',
                fontSize: { xs: '2rem', md: '3rem' }
              }}
            >
              Консультации для родителей
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Полезные материалы от наших специалистов
            </Typography>
          </Box>

          {Object.keys(consultationsGrouped).length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Пока нет доступных консультаций
              </Typography>
            </Paper>
          ) : (
            Object.entries(consultationsGrouped).map(([category, data]) => (
              <Paper
                key={category}
                sx={{
                  mb: 4,
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                }}
              >
                <Box
                  sx={{
                    p: 3,
                    background: 'linear-gradient(135deg, #00ACC1 0%, #81C784 100%)',
                    color: 'white',
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ marginRight: '12px', fontSize: '2rem' }}>
                      {categoryIcons[category] || '📝'}
                    </span>
                    {data.categoryName}
                  </Typography>
                </Box>

                {hasVisualLayout(data.items) ? (
                  <Grid container spacing={3} sx={{ p: 3 }}>
                    {data.items.map((consultation) => (
                      <Grid item xs={12} sm={6} md={4} key={consultation._id}>
                        {consultation.mediaType === 'video' ? (
                          <Card
                            sx={{
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              transition: 'transform 0.3s, box-shadow 0.3s',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: 6,
                              },
                            }}
                          >
                            <ConsultationVideoPlayer
                              videoUrl={consultation.videoUrl || consultation.url}
                              title={consultation.title}
                            />
                            <CardContent sx={{ flexGrow: 1 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 600,
                                  mb: 1,
                                  color: 'text.primary',
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <VideocamIcon sx={{ mr: 1, fontSize: 20, color: 'primary.main' }} />
                                {consultation.title}
                              </Typography>
                              {consultation.description && (
                                <Typography variant="body2" color="text.secondary">
                                  {consultation.description}
                                </Typography>
                              )}
                            </CardContent>
                          </Card>
                        ) : (
                        <Card
                          component={MuiLink}
                          href={consultation.url || consultation.fileUrl || consultation.externalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            textDecoration: 'none',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            transition: 'transform 0.3s, box-shadow 0.3s',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: 6,
                            },
                          }}
                        >
                          {consultation.imageUrl ? (
                            <CardMedia
                              component="img"
                              height="200"
                              image={getFullMediaSrc(consultation.imageUrl)}
                              alt={consultation.title}
                              onError={(e) => {
                                console.error('Ошибка загрузки изображения:', consultation.imageUrl);
                                e.target.style.display = 'none';
                              }}
                              sx={{ objectFit: 'cover', backgroundColor: '#f5f5f5' }}
                            />
                          ) : (
                            <Box
                              sx={{
                                height: 200,
                                backgroundColor: 'rgba(0, 172, 193, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <ImageIcon sx={{ fontSize: 60, color: 'primary.main', opacity: 0.3 }} />
                            </Box>
                          )}
                          <CardContent sx={{ flexGrow: 1 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                mb: 1,
                                color: 'text.primary',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <LinkIcon sx={{ mr: 1, fontSize: 20, color: 'primary.main' }} />
                              {consultation.title}
                            </Typography>
                            {consultation.description && (
                              <Typography variant="body2" color="text.secondary">
                                {consultation.description}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                        )}
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <List sx={{ p: 0 }}>
                    {data.items.map((consultation, index) => (
                      <React.Fragment key={consultation._id}>
                        {index > 0 && <Divider />}
                        {consultation.mediaType === 'video' ? (
                          <Box sx={{ py: 2, px: 3 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <VideocamIcon sx={{ mr: 1, fontSize: 20, color: 'primary.main' }} />
                              {consultation.title}
                            </Typography>
                            <ConsultationVideoPlayer
                              videoUrl={consultation.videoUrl || consultation.url}
                              title={consultation.title}
                            />
                            {consultation.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                {consultation.description}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                        <ListItem
                          component={MuiLink}
                          href={consultation.url || consultation.fileUrl || consultation.externalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            py: 2,
                            px: 3,
                            textDecoration: 'none',
                            color: 'inherit',
                            transition: 'background-color 0.3s',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 172, 193, 0.05)',
                            },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  fontSize: '1.1rem',
                                  fontWeight: 500,
                                  color: 'text.primary',
                                }}
                              >
                                <LinkIcon sx={{ mr: 1, fontSize: 20, color: 'primary.main' }} />
                                {consultation.title}
                              </Box>
                            }
                            secondary={consultation.description}
                          />
                        </ListItem>
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Paper>
            ))
          )}

         
        </Container>
      </Box>
      <Footer />
    </Box>
  );
};

export default ConsultationsListPage;
